import { useRef, useState } from 'react';

import { api } from '../../services/api.js';
import { useChat } from '../../hooks/useChat.js';
import type { Message, MediaReference } from '../../types';
import MediaContextMenu from './MediaContextMenu.js';

interface MessageBubbleProps {
  message: Message;
  onReply?: (message: Message) => void;
}

const fileTypeIcon = (type: MediaReference['type']): string => {
  switch (type) {
    case 'audio':
      return '\uD83C\uDFB5';
    case 'video':
      return '\uD83D\uDCF9';
    case 'document':
      return '\uD83D\uDCC4';
    case 'image':
      return '\uD83D\uDDBC\uFE0F';
    case 'sticker':
      return '\u2728';
    default:
      return '\uD83D\uDCC1';
  }
};

const downloadMedia = (url: string, filename?: string): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'bijlage';
  anchor.target = '_blank';
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const LONG_PRESS_MS = 500;

const countReactions = (reactions?: Message['reactions']): { emoji: string; count: number }[] => {
  if (!reactions || reactions.length === 0) return [];
  const counts = new Map<string, Set<string>>();
  for (const { emoji, userId } of reactions) {
    const set = counts.get(emoji) ?? new Set<string>();
    set.add(userId);
    counts.set(emoji, set);
  }
  return [...counts.entries()]
    .map(([emoji, users]) => ({ emoji, count: users.size }))
    .sort((a, b) => b.count - a.count);
};

const MessageReplyReference = ({ message }: { message: Message }) => {
  const { messages } = useChat();
  const original = messages.find((m) => m.id === message.replyToMessageId);
  const preview = original
    ? original.content.slice(0, 60) || (original.media ? 'Media' : '')
    : 'Origineel bericht';
  const sender = original
    ? original.direction === 'outbound'
      ? 'Jij'
      : 'Contact'
    : 'Onbekend';
  return (
    <div className="message-reply-reference">
      <span className="message-reply-sender">{sender}</span>
      <span className="message-reply-preview">{preview}</span>
    </div>
  );
};

const MessageReactions = ({ reactions }: { reactions?: Message['reactions'] }) => {
  const counts = countReactions(reactions);
  if (counts.length === 0) return null;
  return (
    <div className="message-reactions">
      {counts.map(({ emoji, count }) => (
        <span key={emoji} className="message-reaction" aria-label={`${count} reacties met ${emoji}`}>
          {emoji} {count > 1 ? count : ''}
        </span>
      ))}
    </div>
  );
};

const MenuTrigger = ({
  onClick,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <button
    type="button"
    className="media-menu-trigger"
    aria-label="Media-opties"
    onClick={(event) => {
      event.stopPropagation();
      onClick(event);
    }}
  >
    {'\u2304'}
  </button>
);

const ForwardPicker = ({
  chats,
  onSelect,
  onClose,
}: {
  chats: { id: string; title: string }[];
  onSelect: (chatId: string) => void;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState('');
  const filtered = chats.filter((chat) => chat.title.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="forward-picker-overlay" onClick={onClose}>
      <div className="forward-picker" onClick={(event) => event.stopPropagation()}>
        <div className="forward-picker-header">
          <span>Doorsturen naar</span>
          <button type="button" aria-label="Sluiten" onClick={onClose}>
            ×
          </button>
        </div>
        <input
          type="text"
          placeholder="Zoek chat..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="forward-picker-input"
        />
        <ul className="forward-picker-list">
          {filtered.map((chat) => (
            <li key={chat.id}>
              <button type="button" onClick={() => onSelect(chat.id)}>
                {chat.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MediaRenderer = ({
  message,
  onReply,
}: {
  message: Message;
  onReply?: ((message: Message) => void) | undefined;
}) => {
  const media = message.media!;
  const url = media.localPath
    ? api.mediaLocalUrl(message.id)
    : api.mediaUrl(message.id, media.providerMediaId ?? '');
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [showForward, setShowForward] = useState(false);
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { chats, reload } = useChat();
  const chat = chats.find((c) => c.id === message.chatId);

  const closeMenu = () => setMenu(null);

  const openMenuAtPoint = (x: number, y: number) => {
    setMenu({ x, y });
  };

  const openMenuAtButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    openMenuAtPoint(rect.right, rect.top + window.scrollY);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    openMenuAtPoint(event.clientX, event.clientY);
  };

  const clearLongPress = () => {
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
      touchTimer.current = null;
    }
  };

  const startLongPress = (event: React.TouchEvent) => {
    clearLongPress();
    const touch = event.touches[0];
    if (!touch) return;
    touchTimer.current = setTimeout(() => {
      touchTimer.current = null;
      openMenuAtPoint(touch.clientX, touch.clientY);
    }, LONG_PRESS_MS);
  };

  const handleTouchMove = () => {
    clearLongPress();
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    clearLongPress();
    event.preventDefault();
  };

  const handleTouchCancel = () => {
    clearLongPress();
  };

  const handleReply = () => {
    onReply?.(message);
    closeMenu();
  };

  const handleCopy = async () => {
    const text = message.content || media.filename || url;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
    closeMenu();
  };

  const handleReact = async (emoji: string) => {
    try {
      await api.reactToMessage(message.id, emoji);
      await reload();
    } catch {
      window.alert('Reageren mislukt.');
    }
    closeMenu();
  };

  const handleForward = () => {
    closeMenu();
    setShowForward(true);
  };

  const handlePin = async () => {
    try {
      await api.updateMessage(message.id, { pinned: !message.pinned });
      await reload();
    } catch {
      window.alert('Vastzetten mislukt.');
    }
    closeMenu();
  };

  const handleReport = async () => {
    if (!window.confirm('Dit bericht rapporteren?')) {
      closeMenu();
      return;
    }
    try {
      await api.reportMessage(message.id);
      await reload();
    } catch {
      window.alert('Rapporteren mislukt.');
    }
    closeMenu();
  };

  const handleStar = async () => {
    try {
      await api.updateChat(message.chatId, { favorite: !chat?.favorite });
      await reload();
    } catch {
      window.alert('Markeren mislukt.');
    }
    closeMenu();
  };

  const handleDelete = async () => {
    try {
      await api.deleteMessage(message.id);
      await reload();
    } catch {
      window.alert('Verwijderen mislukt.');
    }
    closeMenu();
  };

  const handleForwardSelect = async (targetChatId: string) => {
    try {
      await api.forwardMessage(message.id, targetChatId);
      setShowForward(false);
      await reload();
    } catch {
      window.alert('Doorsturen mislukt.');
      setShowForward(false);
    }
  };

  const containerProps = {
    onContextMenu: handleContextMenu,
    onTouchStart: startLongPress,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
  };

  const renderMenu = () =>
    menu && (
      <MediaContextMenu
        x={menu.x}
        y={menu.y}
        isStarred={!!chat?.favorite}
        isPinned={!!message.pinned}
        onClose={closeMenu}
        onReply={handleReply}
        onCopy={handleCopy}
        onReact={handleReact}
        onDownload={() => {
          downloadMedia(url, media.filename);
          closeMenu();
        }}
        onForward={handleForward}
        onPin={handlePin}
        onStar={handleStar}
        onReport={handleReport}
        onDelete={handleDelete}
      />
    );

  const element = (() => {
    if (media.type === 'image' && !imageError) {
      return (
        <div className="media-container image" {...containerProps}>
          <MenuTrigger onClick={openMenuAtButton} />
          <img
            src={url}
            alt={message.content || media.filename || 'Foto'}
            className="inline-image"
            loading="lazy"
            onError={() => setImageError(true)}
          />
          {renderMenu()}
        </div>
      );
    }

    if (media.type === 'video' && !videoError) {
      return (
        <div className="media-container video" {...containerProps}>
          <MenuTrigger onClick={openMenuAtButton} />
          <video
            src={url}
            className="inline-video"
            controls
            preload="metadata"
            onError={() => setVideoError(true)}
          />
          {renderMenu()}
        </div>
      );
    }

    if (media.type === 'audio') {
      return (
        <div className="media-container audio" {...containerProps}>
          <MenuTrigger onClick={openMenuAtButton} />
          <div className="voice-message">
            <span className="voice-icon" aria-hidden="true">
              {'\uD83C\uDF99\uFE0F'}
            </span>
            <audio src={url} controls preload="metadata" className="audio-player" />
          </div>
          {renderMenu()}
        </div>
      );
    }

    if (media.type === 'sticker' && !imageError) {
      return (
        <div className="media-container sticker" {...containerProps}>
          <MenuTrigger onClick={openMenuAtButton} />
          <img
            src={url}
            alt={message.content || 'Sticker'}
            className="inline-sticker"
            loading="lazy"
            onError={() => setImageError(true)}
          />
          {renderMenu()}
        </div>
      );
    }

    return (
      <div className="media-container document" {...containerProps}>
        <MenuTrigger onClick={openMenuAtButton} />
        <a
          className="attachment"
          href={url}
          target="_blank"
          rel="noreferrer"
          download={media.filename}
        >
          <span className="attachment-icon" aria-hidden="true">
            {fileTypeIcon(media.type)}
          </span>
          <span className="attachment-name">
            {media.filename ?? (media.type === 'unknown' ? 'Bijlage' : `${media.type}-bijlage`)}
          </span>
          <span className="attachment-download" aria-hidden="true">
            {'\u2B07\uFE0F'}
          </span>
        </a>
        {renderMenu()}
      </div>
    );
  })();

  return (
    <>
      {element}
      {showForward && (
        <ForwardPicker
          chats={chats}
          onSelect={handleForwardSelect}
          onClose={() => setShowForward(false)}
        />
      )}
    </>
  );
};

const MessageBubble = ({ message, onReply }: MessageBubbleProps) => {
  const outbound = message.direction === 'outbound';
  const timestamp = new Date(message.createdAt);
  return (
    <article
      className={`message-bubble ${outbound ? 'outbound' : 'inbound'} ${
        message.status === 'failed' ? 'failed' : ''
      } ${message.pinned ? 'pinned' : ''}`}
      aria-label={outbound ? 'Uitgaand bericht' : 'Inkomend bericht'}
    >
      {message.replyToMessageId && <MessageReplyReference message={message} />}
      {message.content && <p>{message.content}</p>}
      {message.media && <MediaRenderer message={message} onReply={onReply} />}
      <MessageReactions reactions={message.reactions} />
      <footer>
        <time dateTime={message.createdAt}>
          {Number.isNaN(timestamp.getTime())
            ? 'Onbekende tijd'
            : new Intl.DateTimeFormat('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              }).format(timestamp)}
        </time>
        {outbound && (
          <span className={`delivery ${message.status}`} aria-label={`Status: ${message.status}`}>
            {message.status === 'pending'
              ? '…'
              : message.status === 'failed'
                ? '!'
                : message.status === 'read'
                  ? '✓✓'
                  : message.status === 'delivered'
                    ? '✓✓'
                    : '✓'}
          </span>
        )}
      </footer>
    </article>
  );
};

export default MessageBubble;
